// Utility functions ported from Python Calculations.py

export interface Coordinate {
  lat: number;
  lon: number;
}

/**
 * Calculates the bearing between two points.
 * Based on https://gist.github.com/jeromer/2005586
 */
export function calculateInitialCompassBearing(
  pointA: [number, number],
  pointB: [number, number]
): number {
  const lat1 = toRadians(pointA[0]);
  const lat2 = toRadians(pointB[0]);
  const diffLong = toRadians(pointB[1] - pointA[1]);

  const x = Math.sin(diffLong) * Math.cos(lat2);
  const y =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(diffLong);

  const initialBearing = Math.atan2(x, y);
  const compassBearing = (toDegrees(initialBearing) + 360) % 360;

  return compassBearing;
}

/**
 * Calculate distance between two points using Haversine formula
 * Author: Wayne Dyck
 */
export function distance(
  origin: [number, number],
  destination: [number, number]
): number {
  const [lat1, lon1] = origin;
  const [lat2, lon2] = destination;
  const radius = 6371; // km

  const dlat = toRadians(lat2 - lat1);
  const dlon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dlat / 2) * Math.sin(dlat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dlon / 2) *
      Math.sin(dlon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = radius * c;

  return d;
}

/**
 * Calculate pitch angle to look at an object at a given height
 */
export function calculatePitch(
  centerCoord: [number, number],
  currentCoord: [number, number],
  height: number
): number {
  const d = distance(centerCoord, currentCoord);
  return toDegrees(Math.atan(height / d));
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Calculate a destination point given distance and bearing from a start point
 * Uses Haversine formula to calculate point along a great circle
 */
export function calculateDestinationPoint(
  point: [number, number],
  bearing: number,
  distanceKm: number
): [number, number] {
  const radius = 6371; // Earth's radius in km
  const lat1 = toRadians(point[0]);
  const lon1 = toRadians(point[1]);
  const bearingRad = toRadians(bearing);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distanceKm / radius) +
    Math.cos(lat1) * Math.sin(distanceKm / radius) * Math.cos(bearingRad)
  );

  const lon2 = lon1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(distanceKm / radius) * Math.cos(lat1),
    Math.cos(distanceKm / radius) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [toDegrees(lat2), toDegrees(lon2)];
}

/**
 * Generate evenly spaced points along a route path
 * @param routeCoords - The full route coordinates
 * @param targetSpacingMeters - Target spacing between points in meters (default 50m)
 * @returns Array of evenly spaced coordinates with their bearings
 */
export function generateEvenlySpacedPoints(
  routeCoords: [number, number][],
  targetSpacingMeters: number = 20
): Array<{ coord: [number, number]; bearing: number }> {
  if (routeCoords.length < 2) return [];

  const targetSpacingKm = targetSpacingMeters / 1000;
  const result: Array<{ coord: [number, number]; bearing: number }> = [];

  let accumulatedDistance = 0;
  let currentSegmentIndex = 0;
  let distanceIntoSegment = 0;

  // Calculate total route distance and segment lengths
  const segmentLengths: number[] = [];
  let totalDistance = 0;
  
  for (let i = 0; i < routeCoords.length - 1; i++) {
    const segmentLength = distance(routeCoords[i], routeCoords[i + 1]);
    segmentLengths.push(segmentLength);
    totalDistance += segmentLength;
  }

  // Add first point
  const firstBearing = calculateInitialCompassBearing(routeCoords[0], routeCoords[1]);
  result.push({
    coord: routeCoords[0],
    bearing: firstBearing,
  });

  // Generate points at regular intervals
  let nextPointDistance = targetSpacingKm;

  while (nextPointDistance < totalDistance && currentSegmentIndex < routeCoords.length - 1) {
    const segmentLength = segmentLengths[currentSegmentIndex];
    const remainingInSegment = segmentLength - distanceIntoSegment;

    if (nextPointDistance - accumulatedDistance <= remainingInSegment) {
      // Point is in current segment
      const distanceAlongSegment = distanceIntoSegment + (nextPointDistance - accumulatedDistance);

      const segmentStart = routeCoords[currentSegmentIndex];
      const segmentEnd = routeCoords[currentSegmentIndex + 1];
      
      // Calculate bearing for this segment
      const bearing = calculateInitialCompassBearing(segmentStart, segmentEnd);
      
      // Interpolate position along segment
      const interpolatedCoord = calculateDestinationPoint(
        segmentStart,
        bearing,
        distanceAlongSegment
      );

      result.push({
        coord: interpolatedCoord,
        bearing: bearing,
      });

      nextPointDistance += targetSpacingKm;
    } else {
      // Move to next segment
      accumulatedDistance += remainingInSegment;
      distanceIntoSegment = 0;
      currentSegmentIndex++;
    }
  }

  return result;
}
